import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  DialogContentText,
} from '@mui/material';
import { Add, Delete, Warning, Edit, Image as ImageIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import bannerService from '../../../services/BannerService';
import BannerForm from './BannerForm';
import { stripHtml } from '../../../utils/stingUtils';

const getBannerImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return '';

  // Permite URLs completas y previews base64 sin tocarlas.
  if (
    imagePath.startsWith('http://') ||
    imagePath.startsWith('https://') ||
    imagePath.startsWith('data:')
  ) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/\\/g, '/');
  const cleanPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  return `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
};

/** El API puede devolver un array o un objeto envuelto; registros viejos pueden no traer orden. */
const toBannerList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

const normalizeOrden = (b) => {
  const o = b?.orden ?? b?.Orden;
  if (o === null || o === undefined || o === '') return 0;
  const n = Number(o);
  return Number.isFinite(n) ? n : 0;
};

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    bannerId: null,
    bannerText: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bannerService.getBanners();
      const list = toBannerList(data);
      const normalized = list.map((b) => ({
        ...b,
        orden: normalizeOrden(b),
      }));
      setBanners(normalized);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.message ||
        'Error al cargar los banners';
      setError(typeof msg === 'string' ? msg : 'Error al cargar los banners');
      console.error('Error fetching banners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditingData(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingData(null);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setOpenDialog(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteConfirmDialog({
      open: true,
      bannerId: row.id,
      bannerText: row.text || 'este banner',
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      bannerId: null,
      bannerText: '',
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmDialog.bannerId) return;
    try {
      setIsDeleting(true);
      await bannerService.deleteBanner(deleteConfirmDialog.bannerId);
      await fetchBanners();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error eliminando banner:', err);
      setError('Error al eliminar el banner');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    handleCloseDialog();
    fetchBanners();
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'orden',
      headerName: 'Orden',
      width: 100,
      type: 'number',
    },
    {
      field: 'imagePath',
      headerName: 'Imagen',
      width: 150,
      renderCell: (params) => {
        const imageUrl = getBannerImageUrl(params.value);
        return imageUrl ? (
          <Box className="flex items-center justify-center w-full h-full p-2">
            <img
              src={imageUrl}
              alt="Banner"
              className="max-w-full max-h-16 object-contain rounded"
              onError={(e) => {
                e.target.src = '/img/placeholder.png';
              }}
            />
          </Box>
        ) : (
          <Box className="flex items-center justify-center w-full h-full">
            <ImageIcon className="text-gray-400" />
          </Box>
        );
      },
    },
    {
      field: 'text',
      headerName: 'Texto',
      flex: 1,
      renderCell: (params) => (
        <Typography className="text-sm" noWrap>
          {params.value || 'Sin texto'}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'postId',
      headerName: 'Publicacion',
      width: 140,
      valueGetter: (_, row) =>
        row?.postId ??
        row?.PostId ??
        row?.postID ??
        row?.publicationId ??
        row?.PublicationId ??
        null,
      renderCell: (params) => (
        params.value
          ? <Chip label={`#${params.value}`} size="small" color="info" />
          : <Typography variant="caption" color="text.secondary">Sin vincular</Typography>
      ),
    },
    {
      field: 'courseTitle',
      headerName: 'Curso',
      width: 200,
      valueGetter: (_, row) => {
        const title = row?.courseTitle || row?.CourseTitle;
        if (title) return stripHtml(title);
        return row?.courseId || row?.CourseId || null;
      },
      renderCell: (params) => (
        params.value
          ? <Chip label={params.value} size="small" color="success" sx={{ maxWidth: '100%' }} />
          : <Typography variant="caption" color="text.secondary">Sin vincular</Typography>
      ),
    },
    {
      field: 'createdByName',
      headerName: 'Creado por',
      width: 150,
    },
    {
      field: 'createdAt',
      headerName: 'Creado',
      width: 160,
      valueGetter: (_, row) => row?.createdAt ? new Date(row.createdAt).toLocaleDateString('es-ES') : 'N/A',
    },
    {
      field: 'updatedAt',
      headerName: 'Actualizado',
      width: 160,
      valueGetter: (_, row) => row?.updatedAt ? new Date(row.updatedAt).toLocaleDateString('es-ES') : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 280,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          <Button
            size="small"
            variant="contained"
            startIcon={<Edit />}
            onClick={() => handleEdit(params.row)}
            className="background-green-aqua hover:bg-green-600 shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-white"
            sx={{
              minWidth: '80px',
              height: '32px',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(77, 171, 164, 0.3)',
              },
            }}
          >
            Editar
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Delete />}
            onClick={() => handleDeleteClick(params.row)}
            className="bg-red-600 hover:bg-red-700 text-white transition-all duration-200 font-semibold"
            sx={{
              minWidth: '80px',
              height: '32px',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
              },
            }}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <CircularProgress />
        <Typography color="text.secondary">Cargando banners...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center min-h-[400px]">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="p-6 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg space-y-6">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box className="flex items-center gap-3">
          <Box className="w-8 h-8 bg-green-aqua rounded-lg flex items-center justify-center">
            <ImageIcon className="text-white" />
          </Box>
          <Typography variant="h5" className="font-bold text-green-aqua">
            Gestión de Banners
          </Typography>
        </Box>
        <Button
          onClick={handleOpenDialog}
          className="background-green-aqua hover:bg-green-600 hover:shadow-lg transition-all duration-200 font-semibold text-white"
          startIcon={<Add />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            padding: '10px 20px',
            height: '44px',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(77, 171, 164, 0.4)',
            },
          }}
        >
          Nuevo Banner
        </Button>
      </Stack>

      <Box className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="h-[600px]">
          <DataGrid
            rows={banners}
            columns={columns}
            pageSizeOptions={[5, 10, 20]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: {
                sortModel: [{ field: 'orden', sort: 'asc' }],
              },
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#4DABA4',
                color: 'black',
                fontWeight: 'bold',
                fontSize: '14px',
                borderBottom: '3px solid #3a8a84',
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'black',
                  fontWeight: 'bold',
                  fontSize: '14px',
                },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e5e7eb',
                fontSize: '13px',
                padding: '12px 16px',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f0f9ff',
                transition: 'background-color 0.2s ease',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid #4DABA4',
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-virtualScroller': {
                backgroundColor: '#ffffff',
              },
              '& .MuiDataGrid-main': {
                backgroundColor: '#ffffff',
              },
            }}
          />
        </div>
      </Box>

      {/* Diálogo para crear/editar banner */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle className="background-green-aqua flex items-center gap-2 text-white">
          <ImageIcon />
          {editingData ? 'Editar Banner' : 'Crear Nuevo Banner'}
        </DialogTitle>
        <DialogContent dividers className="bg-gray-50">
          <BannerForm
            initialData={editingData}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center gap-2 text-red-600 bg-red-50 border-b border-red-200">
          <Warning className="text-red-500" />
          <Typography variant="h6" className="font-bold">
            Confirmar eliminación
          </Typography>
        </DialogTitle>
        <DialogContent className="bg-white py-6">
          <DialogContentText className="text-gray-700 mb-4">
            ¿Estás seguro de que deseas eliminar el banner{' '}
            <strong className="text-red-600">"{deleteConfirmDialog.bannerText}"</strong>?
          </DialogContentText>
          <DialogContentText className="text-gray-500 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            ⚠️ Esta acción no se puede deshacer. El banner será eliminado permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="p-4 gap-3 bg-gray-50">
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            className="text-gray-700 border-gray-300 hover:bg-gray-100 transition-all duration-200 font-semibold"
            disabled={isDeleting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              borderWidth: '2px',
              minWidth: '100px',
              height: '40px',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(107, 114, 128, 0.2)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            className="bg-red-600 hover:bg-red-700 text-white transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            disabled={isDeleting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: '120px',
              height: '40px',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
              },
            }}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Banners;

